import React, { useState } from 'react';
import {
  Card as MuiCard,
  CardMedia,
  CardContent,
  Typography,
  Box,
  Chip,
  IconButton,
  Tooltip,
  CardActionArea,
  useTheme,
  Popover,
  Button,
} from '@mui/material';
import {
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Add as AddIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { Card as CardType } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useTheme as useCustomTheme } from '../contexts/ThemeContext';
import { useCart } from '../contexts/CartContext';
import { useNavigate } from 'react-router-dom';

interface CardProps {
  card: CardType;
  onCardClick: (card: CardType) => void;
}

const Card: React.FC<CardProps> = ({ card, onCardClick }) => {
  const { user, saveCard, removeSavedCard, isCardSaved } = useAuth();
  const { addToCart } = useCart();
  const navigate = useNavigate();
  const isSaved = isCardSaved(card.id);
  const { mode } = useCustomTheme();
  const muiTheme = useTheme();
  
  // Hover state for popup
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isPopupHovered, setIsPopupHovered] = useState(false);
  
  // Determine proper text colors based on theme mode
  const isLightMode = mode === 'light';
  const priceColor = isLightMode ? '#000000' : '#FFFFFF';
  const secondaryTextColor = isLightMode ? muiTheme.palette.text.secondary : '#FFFFFF';

  const handleSaveClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isSaved) {
      removeSavedCard(card.id);
    } else {
      saveCard(card.id);
    }
  };

  const handleCardClick = () => {
    onCardClick(card);
  };

  const handleMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
    setIsHovered(true);
    setAnchorEl(event.currentTarget);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    // Don't close immediately, let the popup handle its own hover
  };

  const handlePopupMouseEnter = () => {
    setIsPopupHovered(true);
  };

  const handlePopupMouseLeave = () => {
    setIsPopupHovered(false);
    // Close popup when leaving both card and popup
    if (!isHovered) {
      setAnchorEl(null);
    }
  };

  const handleAddToWatchlist = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isSaved) {
      saveCard(card.id);
    }
    setIsHovered(false);
    setIsPopupHovered(false);
    setAnchorEl(null);
  };

  const formatPrice = (price: number) => {
    if (typeof price !== 'number' || isNaN(price)) {
      return '$0.00';
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(price);
  };

  const getConditionColor = (condition: string) => {
    if (!condition || typeof condition !== 'string') {
      return 'default';
    }
    const conditionLower = condition.toLowerCase();
    if (conditionLower.includes('mint') || conditionLower.includes('10')) return 'success';
    if (conditionLower.includes('near mint') || conditionLower.includes('9')) return 'primary';
    if (conditionLower.includes('excellent') || conditionLower.includes('8')) return 'warning';
    return 'default';
  };

  // Add defensive programming to prevent runtime errors
  if (!card || !card.id) {
    return (
      <MuiCard sx={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            Invalid card data
          </Typography>
        </CardContent>
      </MuiCard>
    );
  }

  return (
    <>
      <MuiCard
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          cursor: 'pointer',
          border: 'none',
          boxShadow: 'none',
          transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: 8,
          },
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <CardActionArea onClick={handleCardClick} sx={{ flexGrow: 1 }}>
          <CardMedia
            component="img"
            height="260"
            image={card.imageUrl || 'https://via.placeholder.com/300x400?text=Card+Image'}
            alt={card.title || 'Sports Card'}
            sx={{
              objectFit: 'contain',
              backgroundColor: isLightMode ? '#f8f9fa' : '#2d3748',
              padding: 1,
              borderBottom: '1px solid rgba(255,255,255,0.05)',
            }}
            onError={(e) => {
              // Fallback to placeholder if image fails to load
              const target = e.target as HTMLImageElement;
              target.src = 'https://via.placeholder.com/300x400?text=Card+Image';
            }}
          />
          <CardContent sx={{ flexGrow: 1 }}>
            <Typography variant="h6" component="div" noWrap>
              {card.player || 'Unknown Player'}
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ color: secondaryTextColor }} 
              gutterBottom
            >
              {card.team || 'Unknown Team'} • {card.year || 'N/A'} • {card.brand || 'Unknown Brand'}
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Chip
                label={card.isGraded ? card.grade : card.condition}
                size="small"
                color={getConditionColor(card.isGraded ? card.grade || '' : card.condition)}
                variant={card.isGraded ? 'filled' : 'outlined'}
              />
              {card.isGraded && (
                <Chip
                  label="Graded"
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              )}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography 
                variant="h6" 
                fontWeight="bold"
                sx={{ color: priceColor }}
              >
                {formatPrice(card.price)}
              </Typography>

              {/* Price trend indicator */}
              {card.priceChange !== undefined && !isNaN(card.priceChange) && (
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  bgcolor: card.isHot ? 'rgba(46, 125, 50, 0.1)' : 'rgba(211, 47, 47, 0.1)', 
                  px: 1, 
                  py: 0.5, 
                  borderRadius: 1,
                }}>
                  {card.isHot ? (
                    <TrendingUpIcon sx={{ fontSize: '1rem', color: '#4caf50', mr: 0.5 }} />
                  ) : (
                    <TrendingDownIcon sx={{ fontSize: '1rem', color: '#f44336', mr: 0.5 }} />
                  )}
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: 'bold', 
                      color: card.isHot ? '#4caf50' : '#f44336',
                    }}
                  >
                    {card.priceChange > 0 ? '+' : ''}{card.priceChange.toFixed(1)}%
                  </Typography>
                </Box>
              )}
            </Box>

            <Typography
              variant="body2"
              sx={{
                color: secondaryTextColor,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                mt: 1,
              }}
            >
              {card.description}
            </Typography>
          </CardContent>
        </CardActionArea>

        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            display: 'flex',
            gap: 1,
          }}
        >
          {user && (
            <Tooltip title={isSaved ? 'Remove from saved' : 'Save card'}>
              <IconButton
                size="small"
                onClick={handleSaveClick}
                sx={{
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  color: isSaved ? 'primary.main' : 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                  },
                }}
              >
                {isSaved ? <FavoriteIcon /> : <FavoriteBorderIcon />}
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </MuiCard>

      {/* Hover Popup */}
      <Popover
        open={(isHovered || isPopupHovered) && !isSaved && !!user}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        sx={{
          pointerEvents: 'none',
          '& .MuiPopover-paper': {
            pointerEvents: 'auto',
            backgroundColor: isLightMode ? '#FFFFFF' : '#1A1A1A',
            border: `1px solid ${isLightMode ? '#E0E0E0' : '#333333'}`,
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            p: 2,
            minWidth: 200,
          },
        }}
        onClose={() => {
          setIsHovered(false);
          setIsPopupHovered(false);
          setAnchorEl(null);
        }}
      >
        <Box 
          sx={{ textAlign: 'center' }}
          onMouseEnter={handlePopupMouseEnter}
          onMouseLeave={handlePopupMouseLeave}
        >
          <VisibilityIcon 
            sx={{ 
              fontSize: 32, 
              color: 'primary.main', 
              mb: 1 
            }} 
          />
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 600, 
              mb: 1,
              color: isLightMode ? '#000000' : '#FFFFFF',
            }}
          >
            {card.player || 'Unknown Player'}
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              color: secondaryTextColor, 
              mb: 2 
            }}
          >
            {formatPrice(card.price)}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Button
              variant="contained"
              onClick={(e) => {
                e.stopPropagation();
                addToCart(card);
                navigate('/checkout');
                setIsHovered(false);
                setIsPopupHovered(false);
                setAnchorEl(null);
              }}
              sx={{
                width: '100%',
                py: 1,
                fontWeight: 600,
                backgroundColor: 'success.main',
                '&:hover': {
                  backgroundColor: 'success.dark',
                },
              }}
            >
              Buy Now with Stripe
            </Button>
            <Button
              variant="outlined"
              onClick={(e) => {
                e.stopPropagation();
                addToCart(card);
                navigate('/cart');
                setIsHovered(false);
                setIsPopupHovered(false);
                setAnchorEl(null);
              }}
              sx={{
                width: '100%',
                py: 1,
                fontWeight: 600,
                borderColor: 'primary.main',
                color: 'primary.main',
                '&:hover': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                },
              }}
            >
              Add to Cart
            </Button>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddToWatchlist}
              sx={{
                width: '100%',
                py: 1,
                fontWeight: 600,
                borderColor: 'secondary.main',
                color: 'secondary.main',
                '&:hover': {
                  backgroundColor: 'secondary.main',
                  color: 'white',
                },
              }}
            >
              Add to Watchlist
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default Card; 